import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { useAppSelector, useAppDispatch } from './redux';
import {
  selectAnalysisProgress, selectAnalysisResults, startAnalysis, setCurrentFlow,
  setOverallProgress, updateFlowStatus, addAnalysisMessage, setAnalysisError,
  completeAnalysis, cancelAnalysis, setAnalysisResults, setWizardStep, setCurrentSession,
  setInputData,
  clearAnalysisError,
} from '../features/drbfm/drbfmSlice';
import { drbfmService, startAnalysisWithFetch } from '../services/drbfm.service';
import { mainlineApi } from '../services/drbfm.mainline';
import { analysisCommandFailure, isAnalysisSourceFailure } from '../services/drbfm.analysis';
import type { AnalyzeRequest, FlowProgress, PredefinedPartLabel } from '../types';

interface Completion {
  sessionId: string;
  initializeWorkspace: boolean;
  requestId: string;
  expectedVersion?: string;
  owner: string;
}

export const useDRBFMAnalysis = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const progress = useAppSelector(selectAnalysisProgress);
  const results = useAppSelector(selectAnalysisResults);
  const owner = useAppSelector((state) => `${state.auth.user?.tenantId}:${state.auth.user?.id}`);
  const currentOwner = useRef(owner);
  currentOwner.current = owner;
  const abortControllerRef = useRef<AbortController | null>(null);
  const generation = useRef(0);
  const running = useRef(false);
  const pending = useRef<Completion | null>(null);
  const sourceFailure = useRef<string | null>(null);
  const [completionPending, setCompletionPending] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    running.current = false;
    pending.current = null;
    sourceFailure.current = null;
    setIsRunning(false);
    setCompletionPending(false);
    return () => {
      generation.current += 1;
      abortControllerRef.current?.abort();
    };
  }, [owner]);

  const clearSourceError = useCallback(() => {
    if (running.current || pending.current) return;
    if (sourceFailure.current) dispatch(clearAnalysisError(sourceFailure.current));
    sourceFailure.current = null;
  }, [dispatch]);

  const finish = useCallback(async (completion: Completion, run: number): Promise<boolean> => {
    const active = () => generation.current === run && currentOwner.current === completion.owner;
    try {
      if (completion.initializeWorkspace) {
        const state = await mainlineApi.state(completion.sessionId);
        if (!active()) return false;
        // Reuse the same version and request id after an uncertain HTTP outcome.
        completion.expectedVersion ??= state.version;
        const response = await mainlineApi.command(completion.sessionId, {
          action: 'initialize_review_workspace', request_id: completion.requestId,
          expected_version: completion.expectedVersion,
        });
        if (!active()) return false;
        if (response.result.initialized !== true) throw new Error('initialization_not_confirmed');
      }
      const loaded = await drbfmService.getSessionResults(completion.sessionId);
      if (!active()) return false;
      if (loaded.session.id !== completion.sessionId || loaded.session.status !== 'completed') {
        throw new Error('analysis_completion_not_confirmed');
      }
      dispatch(setCurrentSession(loaded.session));
      dispatch(setAnalysisResults(loaded));
      dispatch(completeAnalysis());
      pending.current = null;
      sourceFailure.current = null;
      setCompletionPending(false);
      return true;
    } catch (error) {
      if (active()) {
        const message = completion.initializeWorkspace
          ? analysisCommandFailure(t, error) : t('drbfm.firstRun.resultsLoadFailed');
        sourceFailure.current = isAnalysisSourceFailure(error) ? message : null;
        dispatch(setAnalysisError(message));
        dispatch(setWizardStep('input'));
        setCompletionPending(true);
      }
      return false;
    }
  }, [dispatch, t]);

  const runAnalysisRequest = useCallback(async (
    sessionId: string, request: AnalyzeRequest, initializeWorkspace = false,
  ): Promise<boolean> => {
    if (running.current) return false;
    running.current = true;
    const run = ++generation.current;
    const runOwner = currentOwner.current;
    const controller = new AbortController();
    abortControllerRef.current = controller;
    pending.current = null;
    sourceFailure.current = null;
    setCompletionPending(false);
    dispatch(setAnalysisResults(null));
    dispatch(startAnalysis());
    setIsRunning(true);
    let succeeded = false;
    const active = () => generation.current === run && !controller.signal.aborted && currentOwner.current === runOwner;
    try {
      await startAnalysisWithFetch(sessionId, request, {
        onProgress: (event) => {
          if (!active()) return;
          const flow = `flow${event.flow}` as keyof FlowProgress;
          const status = ['flow_complete', 'cad_link_complete', 'complete', 'no_changes_found'].includes(event.type)
            ? 'completed' : ['error', 'cad_link_failed'].includes(event.type) ? 'failed' : 'in_progress';
          dispatch(setCurrentFlow(event.flow));
          dispatch(setOverallProgress(event.progress));
          dispatch(updateFlowStatus({ flow, status }));
          dispatch(addAnalysisMessage(event.message));
          if (event.type === 'cad_link_failed'
            || (event.type === 'cad_link_complete' && event.data?.unresolvedFailureModeIds?.length)) {
            dispatch(setAnalysisError(event.message));
          }
        },
        onComplete: async () => {
          if (!active()) return;
          const completion: Completion = {
            sessionId, initializeWorkspace: initializeWorkspace && !!request.sources, requestId: uuidv4(), owner: runOwner,
          };
          pending.current = completion;
          succeeded = await finish(completion, run);
        },
        onError: (message, code) => {
          if (!active()) return;
          sourceFailure.current = isAnalysisSourceFailure(code) ? message : null;
          if (request.sources && (code === 'source_forbidden' || code === 'source_not_found')) {
            dispatch(setInputData({ oldSpecification: '', newSpecification: '' }));
          }
          dispatch(setAnalysisError(message));
          dispatch(setWizardStep('input'));
        },
      }, controller.signal, t);
    } catch {
      if (active()) {
        sourceFailure.current = null;
        dispatch(setAnalysisError(t('drbfm.firstRun.analysisFailed')));
        dispatch(setWizardStep('input'));
      }
    } finally {
      if (generation.current === run) {
        running.current = false;
        setIsRunning(false);
        abortControllerRef.current = null;
      }
    }
    return succeeded;
  }, [dispatch, finish, t]);

  const runAnalysis = useCallback((
    sessionId: string, oldSpecification: string, newSpecification: string,
    cadModelId?: string, predefinedPartLabels?: PredefinedPartLabel[],
  ) => runAnalysisRequest(sessionId, {
    oldSpecification, newSpecification, cadModelId, predefinedPartLabels,
  }), [runAnalysisRequest]);

  const retryCompletion = useCallback(async () => {
    if (running.current || !pending.current) return false;
    running.current = true;
    setIsRunning(true);
    const run = ++generation.current;
    try {
      return await finish(pending.current, run);
    } finally {
      if (generation.current === run) {
        running.current = false;
        setIsRunning(false);
      }
    }
  }, [finish]);

  const discardCompletion = useCallback(() => {
    pending.current = null;
    setCompletionPending(false);
  }, []);

  const cancel = useCallback(() => {
    generation.current += 1;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    running.current = false;
    pending.current = null;
    setCompletionPending(false);
    dispatch(cancelAnalysis());
    setIsRunning(false);
  }, [dispatch]);

  return { progress, results, isRunning, completionPending, runAnalysis, runAnalysisRequest,
    retryCompletion, discardCompletion, clearSourceError, cancel };
};
